import { parseDateParam, shiftDays, toISODate } from "@calendar/weekDates";
import { CaptureNodesList } from "@capture/CaptureNodesList";
import { CaptureTimeline } from "@capture/CaptureTimeline";
import { RoomAudioGateList } from "@capture/RoomAudioGateList";
import { RoomCaptureList } from "@capture/RoomCaptureList";
import { AsyncBoundary } from "@components/AsyncBoundary";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  PanelError,
  SkeletonStack,
} from "@scribenote/brian-design-system";
import { useClinicDay } from "@utils/use-clinic-day";
import { format } from "date-fns";
import { useState } from "react";

export const CapturePanel = () => {
  // An offset, not a date, so 0 tracks clinic-today across midnight and timezone load.
  const { todayKey } = useClinicDay();
  const [dayOffset, setDayOffset] = useState(0);
  const dateKey =
    dayOffset === 0
      ? todayKey
      : toISODate(shiftDays(parseDateParam(todayKey), dayOffset));
  const isToday = dayOffset === 0;
  const date = parseDateParam(dateKey);

  return (
    <div className="flex flex-col gap-6 font-body">
      <section className="flex flex-col gap-3">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-annotation-caps text-annotation">Room capture</h2>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-annotation">
              {format(date, "EEE, MMM d, yyyy")}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset((o) => o - 1)}
              data-testid="capture-prev-day"
              aria-label="Previous day"
            >
              <FontAwesomeIcon icon={faChevronLeft} aria-hidden="true" />
              Prev
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset(0)}
              data-testid="capture-today"
            >
              Today
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDayOffset((o) => o + 1)}
              data-testid="capture-next-day"
              aria-label="Next day"
            >
              Next
              <FontAwesomeIcon icon={faChevronRight} aria-hidden="true" />
            </Button>
          </div>
        </div>

        <AsyncBoundary
          skeleton={<CaptureTimelineSkeleton />}
          fallback={(retry) => (
            <PanelError label="capture timeline" onRetry={retry} />
          )}
          resetKeys={[dateKey]}
        >
          <CaptureTimeline date={dateKey} isToday={isToday} />
        </AsyncBoundary>
      </section>

      <section className="flex flex-col gap-3 border-t border-rule pt-6">
        <h2 className="text-annotation-caps text-annotation">Pause capture</h2>

        <AsyncBoundary
          skeleton={<RoomCaptureSkeleton />}
          fallback={(retry) => (
            <PanelError label="room capture controls" onRetry={retry} />
          )}
        >
          <RoomCaptureList />
        </AsyncBoundary>
      </section>

      <section className="flex flex-col gap-3 border-t border-rule pt-6">
        <h2 className="text-annotation-caps text-annotation">Capture nodes</h2>

        <AsyncBoundary
          skeleton={<CaptureNodesSkeleton />}
          fallback={(retry) => (
            <PanelError label="capture nodes" onRetry={retry} />
          )}
        >
          <CaptureNodesList />
        </AsyncBoundary>
      </section>

      <section className="flex flex-col gap-3 border-t border-rule pt-6">
        <h2 className="text-annotation-caps text-annotation">Audio gate</h2>

        <AsyncBoundary
          skeleton={<RoomAudioGateSkeleton />}
          fallback={(retry) => (
            <PanelError label="audio gate" onRetry={retry} />
          )}
        >
          <RoomAudioGateList />
        </AsyncBoundary>
      </section>
    </div>
  );
};

const CaptureTimelineSkeleton = () => (
  <SkeletonStack count={3} itemClassName="h-8" />
);

const RoomCaptureSkeleton = () => (
  <SkeletonStack count={2} itemClassName="h-10" />
);

const CaptureNodesSkeleton = () => (
  <SkeletonStack count={2} itemClassName="h-10" />
);

const RoomAudioGateSkeleton = () => (
  <SkeletonStack count={2} itemClassName="h-10" />
);
